import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { fr, enUS, es } from "date-fns/locale";
import { CustomDatePicker, CustomDialog, CustomTextField, LimitedTextField } from "../../../../../../common/components";
import { translate, translateDate } from "../../../../../../common/providers";
import styles from "./SectionDetails.module.css";
import { FIELD_TYPES } from "../../constants";
import { AiDetectionsService } from "../../../../../../api";
import { useApi, useEffectOnce, useTrackEvent } from "../../../../../../common/hooks";
import ListUpdate from "./ListUpdate";
import { isNonEmptyArray, isEqual, isValidDate, safeParseDate } from "../../../../../../common/utils";

function customPayloadDate(date) {
	return format(new Date(date), "yyyy-MM-dd");
}
function getValidDate(input) {
	const date = new Date(input);
	return Number.isNaN(date.getTime()) ? null : date;
}
const displayDateTime = (savedTime) => {
	if (isValidDate(getValidDate(savedTime))) {
		return format(savedTime, "yyyy-MM-dd HH:mm:ss");
	}
	return "";
};
export const UpdateValueDialog = ({
	fieldType,
	fieldUuid,
	isMultipleChoice = false,
	open,
	selectedPageId,
	value,
	onClose,
	onSubmit,
}) => {
	const language = useSelector(({ context }) => context.language.value);
	const localeLanguage = useMemo(
		() => (language === "en" && enUS) || (language === "fr" && fr) || (language === "esp" && es) || enUS,
		[language]
	);
	const [localValue, setLocalValue] = useState("");
	const projectId = useSelector(({ context }) => context.project?.id);
	const trackEvent = useTrackEvent();
	const { call: updateCriteriaValue } = useApi(AiDetectionsService.updateCriteriaValue);
	const isValidUpdate = useMemo(() => {
		if ([FIELD_TYPES.TEXT, FIELD_TYPES.DECIMAL, FIELD_TYPES.INTEGER].some((field) => field === fieldType)) {
			return localValue?.length > 0 && localValue !== value?.value;
		}
		if (FIELD_TYPES.DATE === fieldType) {
			const formattedLocalValue = !!localValue && translateDate(displayDateTime(localValue).replaceAll("-", "/"));
			return !!formattedLocalValue && formattedLocalValue !== value?.value;
		}
		if ([FIELD_TYPES.CATEGORY, FIELD_TYPES.LIST].some((field) => field === fieldType)) {
			return (
				isNonEmptyArray(localValue) &&
				!isEqual(
					value.map((val) => val.value),
					localValue.map((val) => val.value)
				)
			);
		}
		return false;
	}, [fieldType, localValue, value]);
	useEffectOnce(
		() => {
			setLocalValue("");
			if (
				[FIELD_TYPES.TEXT, FIELD_TYPES.DECIMAL, FIELD_TYPES.INTEGER].some((field) => field === fieldType) &&
				value?.value &&
				value?.value !== "N/A"
			) {
				setLocalValue(value?.value);
			}
			if (FIELD_TYPES.DATE === fieldType && value?.value) {
				const dateObj = safeParseDate(value?.value);
				setLocalValue(dateObj);
			}
			if (
				[FIELD_TYPES.LIST, FIELD_TYPES.CATEGORY].some((field) => field === fieldType) &&
				isNonEmptyArray(value)
			) {
				setLocalValue(value);
			}
		},
		[open, fieldType, fieldUuid, value],
		() => open && value && fieldUuid && fieldType
	);
	const generatePayload = () => {
		if (
			[FIELD_TYPES.TEXT, FIELD_TYPES.DECIMAL, FIELD_TYPES.INTEGER].some((field) => field === fieldType) &&
			localValue
		) {
			return [{ value: localValue, valueUuid: value?.valueUuid }];
		}
		if (FIELD_TYPES.DATE === fieldType) {
			return [{ value: customPayloadDate(localValue), valueUuid: value?.valueUuid }];
		}
		if (
			[FIELD_TYPES.CATEGORY, FIELD_TYPES.LIST].some((field) => field === fieldType) &&
			isNonEmptyArray(localValue)
		) {
			return localValue;
		}
		return [];
	};
	const handleSubmit = () => {
		const payload = generatePayload();
		if (projectId && fieldUuid && payload) {
			updateCriteriaValue({ projectId, fieldUuid }, payload)
				.then(() => {
					onSubmit();
					onClose();
					trackEvent("Detection_Update_Value");
				})
				.catch(console.error);
		}
	};
	const handleChange = (newValue) => {
		if ([FIELD_TYPES.DECIMAL, FIELD_TYPES.INTEGER].some((field) => field === fieldType)) {
			if (FIELD_TYPES.INTEGER === fieldType && newValue?.target?.value?.includes(".")) {
				return;
			}
			setLocalValue(newValue.target.value);
		}
		if (FIELD_TYPES.DATE === fieldType) {
			const constructedValue = newValue.toISOString();
			setLocalValue(constructedValue);
		}
		if (FIELD_TYPES.TEXT === fieldType) {
			setLocalValue(newValue);
		}
		if ([FIELD_TYPES.CATEGORY, FIELD_TYPES.LIST].some((field) => field === fieldType)) {
			setLocalValue(newValue);
		}
	};
	const inputRenderer = () => {
		switch (fieldType) {
			case FIELD_TYPES.TEXT:
				return (
					<LimitedTextField
						autoFocus
						fullWidth
						multiline
						maxLength={16383}
						rows={30}
						showHelper={false}
						type="text"
						value={localValue}
						onChange={handleChange}
					/>
				);
			case FIELD_TYPES.DATE:
				return (
					<LocalizationProvider adapterLocale={localeLanguage} dateAdapter={AdapterDateFns}>
						<CustomDatePicker
							fullWidth
							className={styles.datePicker}
							inputFormat="dd/MM/yyyy"
							value={getValidDate(localValue)}
							onChange={handleChange}
						/>
					</LocalizationProvider>
				);
			case FIELD_TYPES.LIST:
			case FIELD_TYPES.CATEGORY:
				return (
					<ListUpdate
						currentValue={localValue || []}
						fieldUuid={fieldUuid}
						isMultipleChoice={isMultipleChoice}
						open={open}
						projectId={projectId}
						selectedPageId={selectedPageId}
						onChange={handleChange}
					/>
				);
			case FIELD_TYPES.DECIMAL:
			case FIELD_TYPES.INTEGER:
				return (
					<div className={styles.numberTypeContainer}>
						<span>{translate("dashboards.project-sheet.edit-dialog.type.number")}</span>
						<CustomTextField
							fullWidth
							InputProps={{
								inputProps: {
									step: FIELD_TYPES.DECIMAL === fieldType ? "any" : 1,
								},
							}}
							placeholder="0,00"
							sx={{
								"& input[type=number]::-webkit-outer-spin-button": { display: "none" },
								"& input[type=number]::-webkit-inner-spin-button": { display: "none" },
								"& input[type=number]": { MozAppearance: "textfield" },
							}}
							type="number"
							value={localValue}
							onChange={handleChange}
						/>
					</div>
				);
			default:
				return null;
		}
	};
	return (
		<CustomDialog
			className={styles.associateContent}
			classNameDialog={styles.associateContentDialog}
			open={open}
			submitDisabled={!isValidUpdate}
			submitLabel={translate("common:btn.validate")}
			title={translate("dashboards.project-sheet.section-details.update-value")}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<div>{inputRenderer()}</div>
		</CustomDialog>
	);
};
